import React from "react";
import {
  Box,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  Pie,
  PieChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import analyticsApi from "../api/analyticsApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";

const COLORS = ["#3182ce", "#38a169", "#d69e2e", "#e53e3e", "#805ad5"];

const AnalyticsPage = () => {
  const [showGuide, setShowGuide] = React.useState(false);
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadAnalytics = can(PERFORMANCE_PERMISSIONS.ANALYTICS_READ);

  if (!canReadAnalytics) {
    return (
      <ModulePageShell
        title="Performance Analytics"
        description="Department performance, top performers, rating and campaign insights."
      >
        <EmptyState message="You do not have permission to view performance analytics." />
      </ModulePageShell>
    );
  }

  const overviewQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "overview"],
    queryFn: analyticsApi.getOverview,
  });
  const departmentQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "department"],
    queryFn: analyticsApi.getDepartmentPerformance,
  });
  const topQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "top"],
    queryFn: () => analyticsApi.getTopPerformers({ limit: 10 }),
  });
  const ratingQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "ratings"],
    queryFn: analyticsApi.getRatingDistribution,
  });
  const competencyQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "competency"],
    queryFn: analyticsApi.getCompetencyDistribution,
  });
  const campaignQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "campaign"],
    queryFn: analyticsApi.getCampaignCompletion,
  });
  const relationQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.analytics, "relation"],
    queryFn: analyticsApi.getRelationWiseRatingSpread,
  });

  const loading =
    overviewQuery.isLoading ||
    departmentQuery.isLoading ||
    topQuery.isLoading ||
    ratingQuery.isLoading ||
    competencyQuery.isLoading ||
    campaignQuery.isLoading ||
    relationQuery.isLoading;

  const departmentRows = departmentQuery.data?.data || [];
  const topRows = topQuery.data?.data || [];
  const ratingRows = ratingQuery.data?.data || [];
  const competencyRows = competencyQuery.data?.data || [];
  const campaignRows = campaignQuery.data?.data || [];
  const relationRows = relationQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Performance Analytics"
      description="Department performance, top performers, rating and campaign insights."
      onRefresh={() => {
        overviewQuery.refetch();
        departmentQuery.refetch();
        topQuery.refetch();
        ratingQuery.refetch();
        competencyQuery.refetch();
        campaignQuery.refetch();
        relationQuery.refetch();
      }}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Department Performance
              </Text>
              <Box h="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={Array.isArray(departmentRows) ? departmentRows : []}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3182ce" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Rating Distribution
              </Text>
              <Box h="260px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={Array.isArray(ratingRows) ? ratingRows : []}
                      dataKey="value"
                      nameKey="label"
                      outerRadius={90}
                    >
                      {(ratingRows || []).map((entry, idx) => (
                        <Cell
                          key={`${entry.label}-${idx}`}
                          fill={COLORS[idx % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mt={4}>
            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Top Performers
              </Text>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Employee</Th>
                    <Th>Score</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(topRows || []).map((item) => (
                    <Tr key={item._id || item.employeeId || item.label}>
                      <Td>
                        {item.employeeName ||
                          item.label ||
                          item.employeeId ||
                          "-"}
                      </Td>
                      <Td>{item.score ?? item.value ?? 0}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Campaign Completion
              </Text>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Campaign</Th>
                    <Th>Completion %</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(campaignRows || []).map((item) => (
                    <Tr key={item._id || item.campaignId || item.label}>
                      <Td>{item.campaignName || item.label || "-"}</Td>
                      <Td>{item.completionPercentage ?? item.value ?? 0}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mt={4}>
            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Competency Distribution
              </Text>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Competency</Th>
                    <Th>Value</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(competencyRows || []).map((item) => (
                    <Tr key={item._id || item.competencyId || item.label}>
                      <Td>{item.competencyName || item.label || "-"}</Td>
                      <Td>{item.value ?? item.count ?? 0}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="semibold" mb={3}>
                Relation-wise Rating Spread
              </Text>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Relation</Th>
                    <Th>Average Rating</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(relationRows || []).map((item) => (
                    <Tr key={item._id || item.relationType || item.label}>
                      <Td>{item.relationType || item.label || "-"}</Td>
                      <Td>{item.averageRating ?? item.value ?? 0}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </SimpleGrid>
        </>
      )}
    </ModulePageShell>
  );
};

export default AnalyticsPage;
